import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useSite } from "@/context/SiteContext";
import { Logo } from "@/components/site/Logo";

const BASE_LINKS = [
  { to: "/", label: "Home" },
  { to: "/memberships", label: "Memberships" },
  { to: "/locations", label: "Locations" },
  { to: "/gallery", label: "Gallery" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export function useNavLinks() {
  const { content } = useSite();
  const extra = (content.pages || []).filter((p) => p.enabled).map((p) => ({ to: `/p/${p.slug}`, label: p.title }));
  return [...BASE_LINKS.slice(0, 2), ...extra, ...BASE_LINKS.slice(2)];
}

function PromoBar() {
  const { content, openJoin } = useSite();
  const s = content.settings;
  if (!s.promo_enabled || !s.promo_text) return null;
  return (
    <button onClick={() => openJoin({ type: "membership" })} data-testid="promo-bar"
      className="block w-full bg-[var(--gold)] px-4 py-1.5 text-center font-mono-ui text-[11px] font-bold uppercase tracking-[0.15em] text-black transition-colors duration-200 hover:bg-[var(--gold-hover)]">
      {s.promo_text}
    </button>
  );
}

export function Navbar() {
  const { openJoin } = useSite();
  const links = useNavLinks();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkCls = ({ isActive }) =>
    `font-display text-[15px] font-bold uppercase tracking-[0.12em] transition-colors duration-200 ${isActive ? "text-[var(--gold)]" : "text-white/70 hover:text-white"}`;

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      <PromoBar />
      <div className={`border-b transition-colors duration-300 ${scrolled ? "border-white/10 bg-black/85 backdrop-blur-xl" : "border-transparent bg-black/40 backdrop-blur-md"}`}>
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-8">
          <Link to="/" data-testid="nav-logo"><Logo /></Link>
          <div className="hidden items-center gap-7 lg:flex">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end className={linkCls} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>{l.label}</NavLink>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => openJoin()} className="btn-gold !px-4 !py-2 !text-base" data-testid="nav-join-now-btn">Join Now</button>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="p-2 text-white lg:hidden" aria-label="Open menu" data-testid="nav-mobile-menu-btn"><Menu className="h-6 w-6" /></button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85vw] max-w-sm border-white/10 bg-[#0A0A0A]">
                <SheetTitle className="sr-only">Menu</SheetTitle>
                <div className="mt-10 flex flex-col gap-5">
                  {links.map((l) => (
                    <NavLink key={l.to} to={l.to} end onClick={() => setOpen(false)}
                      className={({ isActive }) => `h-display text-4xl ${isActive ? "text-[var(--gold)]" : "text-white"}`}
                      data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>{l.label}</NavLink>
                  ))}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </nav>
      </div>
    </header>
  );
}
